import React, { useState, useEffect, createRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Col,
    Row,
} from '@snsw/react-component-library';
import {
    ButtonsContainer,
    ErrorSummary,
    ErrorSummaryType,
    EligibilityPageProjectArchetypeValidator,
    ActionType,
    TransactionDetails,
    scrollToTop, ConfirmBeforeExit
} from '../../index';
import styled from 'styled-components';
import { useApplicationContext, useApplicationConfig } from '../../store';
import { gaasApiServiceClientFactory } from '../../services';
import {
    DataSubmissionMapper,
    defaultGaasApplicationDataMapper
} from '../../services/default-gaas-submission-data-service';
import { EligibilityPageProjectArchetype } from '../../Components';
import { getErrorMessage } from '../../Components/Configuration/textConfiguration';

const RequiredWrapper = styled.div`
  margin-top: 1.5rem;
`;

export const GaasEligibilityPage: React.FC<{dataSubmissionMapper?: DataSubmissionMapper}> = props => {
    const history = useHistory();
    const { globalState, dispatch } = useApplicationContext();
    const appConfig = useApplicationConfig();
    const [errors, setErrors] = useState<ErrorSummaryType>({});
    // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
    const [eligibilityCheckResult, setEligibilityCheckResult] = useState(null);
    const [submitPressed, setSubmitPressed] = useState(false);
    const errorSummaryRef = createRef();

    const userInfo = () => {
        // TODO: once authcontext migrates into the library we should useAuthContext() for this
        const info = sessionStorage.getItem('userInfo');
        return info ? JSON.parse(info) : null;
    };

    const validateEligibilityPage = async () => {
        const errorSummary = validateFormFields();
        let eligibilityResult = { message: null, code: null, reasons: [{ code: null }] };
        // Perform eligibility check only if form fields do not have any validation error
        if (Object.keys(errorSummary).length === 0) {
            eligibilityResult = await performEligibilityCheck();
        }

        if (eligibilityResult != null && eligibilityResult.code != null && 'SUCCESS' !== eligibilityResult.code) {

            const claimFailedCode = (eligibilityResult.reasons && eligibilityResult.reasons[0] && eligibilityResult.reasons[0].code) ?
                eligibilityResult.reasons[0].code : 'generalFailure';
            errorSummary.eligibilityCheck = {
                message: getErrorMessage(
                    appConfig,
                    'eligibilityPage',
                    'eligibilityCheck',
                    claimFailedCode,
                    eligibilityResult.message ?? 'The eligibility Check Failed'
                )
            };
        }
        setErrors(errorSummary);
        return errorSummary;
    };

    const onSubmit = async (d: any) => {
        scrollToTop();
        setSubmitPressed(true);
        const errorSummary = await validateEligibilityPage();
        if (Object.keys(errorSummary).length === 0) {
            history.push('/supporting-documents-page');
        }
    };

    const onExit = () => {
        scrollToTop();
        window.location.href = appConfig.links.landingPageUrl;
    };

    const performEligibilityCheck = async () => {
        const payload = (appConfig.backend?.dataSubmissionMapper ?? defaultGaasApplicationDataMapper)(globalState, appConfig);
        try {
            const eligibilityResult = await gaasApiServiceClientFactory(appConfig)
                .checkEligibility(payload.correlationId, payload);
            setEligibilityCheckResult(eligibilityResult);
            if (appConfig?.appConfig?.enableDebug) {
                console.log('DEBUG-eligibility check result:', JSON.stringify(eligibilityResult, null, 2));
            } else if (eligibilityResult.code !== 'SUCCESS') {
                console.log('eligibility result was ', eligibilityResult.code);
            }
            return eligibilityResult;
        } catch (e: any) {
            console.error('An error occurred calling eligibility check API:', e);
            return { code: 'ERROR', message: 'Eligibility check failed', reasons: [ {}] };
        }
    };

    const validateFormFields = () => {
        let errorSummary: ErrorSummaryType = {};

        if (appConfig.projectArchetype !== null && appConfig.projectArchetype !== 'none') {
            errorSummary = EligibilityPageProjectArchetypeValidator(globalState, appConfig.projectArchetype, appConfig);
        }
        return errorSummary;
    };

    const errorSummaryRefRow = () => {
        // scrollToRef(errorSummaryRef);
        createRef();
    };

    // TODO: need code to clear eligibilityCheckResult when the fields it depends on are changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {setErrors(validateFormFields());}, [globalState]);

    // added below to test api integration
    useEffect(() => {
        const initTransaction = async () => {
            const gaasAppApiService = gaasApiServiceClientFactory(appConfig);
            const { correlationId, profileData } = await gaasAppApiService.startTransaction();
            const transactionDetails: TransactionDetails = {
                correlationId: correlationId || '',
                poiReferenceNumber: sessionStorage.getItem('poiReferenceNumber') || ''
            };
            const userInfoObj = userInfo();

            if (appConfig?.appConfig?.enableDebug) {
                console.log('DEBUG correlationId: ', correlationId);
                console.log('DEBUG userInfoObj: ', userInfoObj);
                console.log('DEBUG profileConnectObj: ', profileData);
            }
            dispatch({
                type: ActionType.INITIALISE_TRANSACTION,
                payload: {
                    userInfo: userInfoObj,
                    profileConnectInfo: profileData,
                    transactionDetails: transactionDetails
                }
            });

            // if we are returning to the page and fields have been completed we might want to do an eligibility check API call
            // on loading. (e.g. return from review page, save and resume... )
            // provided, the form fields do not have any validation issue
            await validateEligibilityPage();
        };
        /**
         * only initialise the state if we have not already done so, or a previously initialised app was successfully submitted
         * (since the review page performing submission is not yet in the library you can't see where `submitted` becomes true)
         * otherwise we would generate a new correlation if the customer navigates back to this page.
         */
        if (!globalState?.transactionDetails?.correlationId || globalState.review.submitted) {
            initTransaction().then(() => console.log('initialised'));
        }
    }, []);

    // const onChangeEvent = (fieldName: string) => ( value, d ) => {
    //     eligibility[fieldName] = value;
    //     dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    // };

    return (
        <>
            <Row>
                <Col span={ 6 }>
                    <RequiredWrapper>
                        All fields are required.
                    </RequiredWrapper>
                </Col>
            </Row>
            {submitPressed &&
                <Row ref={ errorSummaryRefRow }>
                    <Col span={ 8 }>
                        <ErrorSummary
                            ref={ errorSummaryRef }
                            errors={ errors }
                            title='We cannot progress your application'
                            headingTemplate=''
                        />
                    </Col>
                </Row>}
            <EligibilityPageProjectArchetype
                type={ appConfig.projectArchetype }
                errors={ errors }
                showErrors={ submitPressed }
            />

            <ButtonsContainer>
                <Button
                    className='button-item'
                    id='nextButton'
                    onClick={ onSubmit }
                    data-testid='submitButton'
                >
                    Continue
                </Button>
            </ButtonsContainer>
            <ConfirmBeforeExit showConfirmOnClose={ true } />
            <Button
                id='backButton'
                onClick={ onExit }
                variant='link'
            >
                Exit
            </Button>
        </>
    );
};
